import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import { Plus, AlertTriangle, RefreshCw, ChevronDown, ChevronUp, ListChecks } from 'lucide-react';
import { processStagesApi, committeesApi, ProcessStageDto, CommitteeDto, handleApiError } from '../../services/committeesService';
import HorizontalTimeline from './HorizontalTimeline';
import StageAccordion from './StageAccordion';
import CommitteeHistory from './CommitteeHistory';
import AddCommitteeModal from '../modals/AddCommitteeModal';
import UpdateDecisionModal from '../modals/UpdateDecisionModal';
import EditProcessStageModal from '../modals/EditProcessStageModal';

interface CommitteesProcessTabProps {
  clientId: number;
  clientName: string;
}

const TabContainer = styled.div`
  direction: rtl;
  min-height: 100%;
`;

const MainContent = styled.div`
  padding: 24px;

  @media (max-width: 767px) {
    padding: 16px;
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);

  @media (max-width: 767px) {
    font-size: 20px;
  }
`;

const CommitteeCountBadge = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: var(--text-tertiary);
`;

const HeaderRight = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: transparent;
  color: var(--primary-color);
  border: 1px solid var(--primary-color);
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: var(--primary-color);
    color: white;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 767px) {
    width: 100%;
    justify-content: center;
  }
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: transparent;
  color: var(--text-secondary);
  border: none;
  border-radius: var(--border-radius-md);
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover:not(:disabled) {
    color: var(--primary-color);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &[data-tooltip]:hover::after {
    content: attr(data-tooltip);
    position: absolute;
    top: 100%;
    right: 50%;
    transform: translateX(50%);
    margin-top: 8px;
    padding: 6px 12px;
    background: var(--text-primary);
    color: white;
    font-size: 12px;
    font-weight: 500;
    border-radius: var(--border-radius-sm);
    white-space: nowrap;
    z-index: 10;
  }
`;


const Section = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid var(--border-color);
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
`;

const CollapsibleContainer = styled.div`
  overflow: hidden;
`;

const CollapsibleHeader = styled.button<{ $isExpanded: boolean }>`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  background: var(--background-secondary);
  border: none;
  border-radius: var(--border-radius-md);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--hover-background);
  }
`;

const CollapsibleTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CollapsibleIcon = styled.span<{ $isExpanded?: boolean }>`
  display: flex;
  align-items: center;
  color: ${props => props.$isExpanded ? 'var(--primary-color)' : 'var(--text-secondary)'};
  transition: all 0.2s ease;
`;

const CollapsibleContent = styled.div<{ $isExpanded: boolean }>`
  max-height: ${props => props.$isExpanded ? '2000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const CollapsibleInner = styled.div`
  padding: 16px 0;
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: rgba(220, 38, 38, 0.1);
  color: #dc2626;
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 20px;
  border: 1px solid #dc2626;
`;

const ErrorIcon = styled.span`
  flex-shrink: 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--text-secondary);
  gap: 12px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-color);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const CommitteesProcessTab: React.FC<CommitteesProcessTabProps> = ({ clientId, clientName }) => {
  // State
  const [stages, setStages] = useState<ProcessStageDto[]>([]);
  const [latestCommittee, setLatestCommittee] = useState<CommitteeDto | null>(null);
  const [allCommittees, setAllCommittees] = useState<CommitteeDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);

  // Modal state
  const [showAddCommitteeModal, setShowAddCommitteeModal] = useState(false);
  const [showUpdateDecisionModal, setShowUpdateDecisionModal] = useState(false);
  const [showEditStageModal, setShowEditStageModal] = useState(false);
  const [selectedStage, setSelectedStage] = useState<ProcessStageDto | null>(null);
  const [committeeToUpdate, setCommitteeToUpdate] = useState<CommitteeDto | null>(null);

  // UI state
  const [isStageDetailsExpanded, setIsStageDetailsExpanded] = useState(false);

  // Calculate committee count - count ALL committees (not just ones with final decisions)
  const totalCommitteesCount = allCommittees.length;
  const canAddCommittee = totalCommitteesCount < 3;

  const loadStages = useCallback(async () => {
    try {
      const existingStages = await processStagesApi.getByClientId(clientId);
      if (existingStages.length === 0) {
        await processStagesApi.initializeClientStages(clientId);
        const newStages = await processStagesApi.getByClientId(clientId);
        setStages(newStages);
      } else {
        setStages(existingStages);
      }
    } catch (err) {
      console.error('Error loading stages:', err);
      setError(handleApiError(err));
    }
  }, [clientId]);

  const loadCommittees = useCallback(async () => {
    try {
      const [latest, all] = await Promise.all([
        committeesApi.getLatestByClientId(clientId),
        committeesApi.getByClientId(clientId)
      ]);
      setLatestCommittee(latest);
      setAllCommittees(all);
    } catch (err) {
      console.error('Error loading committees:', err);
      setError(handleApiError(err));
    }
  }, [clientId]);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([loadStages(), loadCommittees()]);
    } finally {
      setLoading(false);
    }
  }, [loadStages, loadCommittees]);

  useEffect(() => {
    loadAllData();
  }, [clientId, loadAllData]);

  const handleAddCommitteeSuccess = () => {
    loadCommittees();
    setShowAddCommitteeModal(false);
  };

  const handleUpdateDecisionSuccess = () => {
    loadCommittees();
    setShowUpdateDecisionModal(false);
    setCommitteeToUpdate(null);
  };

  const handleUpdateCommittee = (committee: CommitteeDto) => {
    setCommitteeToUpdate(committee);
    setShowUpdateDecisionModal(true);
  };

  const handleDeleteCommittee = async (id: number) => {
    try {
      await committeesApi.delete(id);
      loadCommittees();
    } catch (err) {
      toast.error(handleApiError(err));
    }
  };

  const handleEditStage = (stage: ProcessStageDto) => {
    if (stage.id === 0) {
      toast.error('יש לסנכרן את השלבים לפני עריכה');
      handleSyncStages();
      return;
    }
    setSelectedStage(stage);
    setShowEditStageModal(true);
  };

  const handleEditStageSuccess = () => {
    loadStages();
    setShowEditStageModal(false);
    setSelectedStage(null);
  };

  const handleSyncStages = async () => {
    setIsSyncing(true);
    setError(null);
    try {
      await processStagesApi.syncClientStages(clientId);
      await loadStages();
    } catch (err) {
      console.error('Error syncing stages:', err);
      setError(handleApiError(err));
    } finally {
      setIsSyncing(false);
    }
  };

  if (loading) {
    return (
      <TabContainer>
        <LoadingContainer>
          <LoadingSpinner />
          <span>טוען נתוני תהליך וועדות...</span>
        </LoadingContainer>
      </TabContainer>
    );
  }

  return (
    <TabContainer>
      <MainContent>
        {/* Error Message */}
        {error && (
          <ErrorMessage>
            <ErrorIcon><AlertTriangle size={18} /></ErrorIcon>
            <span>{error}</span>
          </ErrorMessage>
        )}

        {/* Page Header */}
        <PageHeader>
          <HeaderLeft>
            <PageTitle>תהליך וועדות</PageTitle>
            {totalCommitteesCount > 0 && (
              <CommitteeCountBadge>
                {totalCommitteesCount}/3 הערכות
              </CommitteeCountBadge>
            )}
          </HeaderLeft>
          <HeaderRight>
            <PrimaryButton
              onClick={() => setShowAddCommitteeModal(true)}
              disabled={!canAddCommittee}
            >
              <Plus size={16} />
              הוספת וועדה
            </PrimaryButton>
            <IconButton
              onClick={handleSyncStages}
              disabled={isSyncing}
              data-tooltip={isSyncing ? 'מסנכרן...' : 'סנכרון שלבים'}
            >
              <RefreshCw size={18} className={isSyncing ? 'spinning' : ''} />
            </IconButton>
          </HeaderRight>
        </PageHeader>

        {/* Horizontal Timeline */}
        <Section>
          <SectionHeader>
            <SectionTitle>ציר זמן תהליך</SectionTitle>
          </SectionHeader>
          <HorizontalTimeline
            stages={stages}
            onStageClick={handleEditStage}
          />
        </Section>

        {/* Stage Details - Collapsible */}
        <Section>
          <CollapsibleContainer>
            <CollapsibleHeader
              $isExpanded={isStageDetailsExpanded}
              onClick={() => setIsStageDetailsExpanded(!isStageDetailsExpanded)}
            >
              <CollapsibleTitle>
                <ListChecks size={18} />
                פרטי שלבים
              </CollapsibleTitle>
              <CollapsibleIcon $isExpanded={isStageDetailsExpanded}>
                {isStageDetailsExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </CollapsibleIcon>
            </CollapsibleHeader>
            <CollapsibleContent $isExpanded={isStageDetailsExpanded}>
              <CollapsibleInner>
                <StageAccordion
                  stages={stages}
                  committee={latestCommittee}
                  onEditStage={handleEditStage}
                />
              </CollapsibleInner>
            </CollapsibleContent>
          </CollapsibleContainer>
        </Section>

        {/* Committee History */}
        {allCommittees.length > 0 && (
          <Section>
            <CommitteeHistory
              committees={allCommittees}
              onDeleteCommittee={handleDeleteCommittee}
              onUpdateCommittee={handleUpdateCommittee}
              onReassigned={loadCommittees}
            />
          </Section>
        )}
      </MainContent>

      {/* Modals */}
      <AddCommitteeModal
        isOpen={showAddCommitteeModal}
        onClose={() => setShowAddCommitteeModal(false)}
        onSuccess={handleAddCommitteeSuccess}
        clientId={clientId}
      />

      <UpdateDecisionModal
        isOpen={showUpdateDecisionModal}
        onClose={() => {
          setShowUpdateDecisionModal(false);
          setCommitteeToUpdate(null);
        }}
        onSuccess={handleUpdateDecisionSuccess}
        committee={committeeToUpdate}
      />

      {selectedStage && (
        <EditProcessStageModal
          isOpen={showEditStageModal}
          onClose={() => {
            setShowEditStageModal(false);
            setSelectedStage(null);
          }}
          onSuccess={handleEditStageSuccess}
          stage={selectedStage}
        />
      )}
    </TabContainer>
  );
};

export default CommitteesProcessTab;
